import { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, User, Loader2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chat-store';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

interface AIModel {
  id: string;
  name: string;
  provider: string;
  available: boolean;
}

interface AIPanelProps {
  onClose?: () => void;
}

export function AIPanel({ onClose }: AIPanelProps) {
  const [input, setInput] = useState('');
  const [models, setModels] = useState<AIModel[]>([]);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { activePropertyId } = useAuthStore();
  const {
    activeConversationId,
    isStreaming,
    streamingContent,
    selectedModel,
    getActiveConversation,
    startConversation,
    sendMessage,
    setSelectedModel,
  } = useChatStore();

  // Fetch available models on mount
  useEffect(() => {
    async function fetchModels() {
      try {
        const result = await api.ai.getModels();
        if (result?.models) {
          setModels(result.models);
          // Set default model if none selected
          if (!selectedModel && result.default) {
            setSelectedModel(result.default);
          }
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
      }
    }
    fetchModels();
  }, [selectedModel, setSelectedModel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowModelDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentModel = models.find((m) => m.id === selectedModel) || models[0];

  const conversation = getActiveConversation();

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages, streamingContent]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    // Start conversation if needed
    if (!activeConversationId && activePropertyId) {
      startConversation(activePropertyId);
    }

    const message = input.trim();
    setInput('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const suggestions = [
    'How did traffic perform last week?',
    'What are my top pages?',
    'Show me search queries with high impressions',
    'Why did bounce rate increase?',
  ];

  return (
    <div className="flex h-full w-full flex-col bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <span className="font-medium text-foreground tracking-wide uppercase">
              <span className="font-semibold">Chart</span>
              <span className="font-light">Zen</span>
              <span className="font-normal normal-case tracking-normal"> AI</span>
            </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Model selector */}
          {models.length > 0 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowModelDropdown(!showModelDropdown)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <span className="max-w-[100px] truncate">
                  {currentModel?.name || 'Select model'}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>

              {showModelDropdown && (
                <div className="absolute right-0 top-full z-50 mt-1 min-w-[180px] rounded-md border border-border bg-surface py-1 shadow-lg">
                  {models.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        setSelectedModel(model.id);
                        setShowModelDropdown(false);
                      }}
                      disabled={!model.available}
                      className={cn(
                        'flex w-full items-center justify-between px-3 py-1.5 text-left text-sm transition-colors',
                        model.available
                          ? 'text-foreground hover:bg-muted'
                          : 'cursor-not-allowed text-muted-foreground/50',
                        selectedModel === model.id && 'bg-primary/10'
                      )}
                    >
                      <span>{model.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {model.provider}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {onClose && (
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {!conversation?.messages.length ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 font-medium text-foreground">Ask me anything</h3>
            <p className="mb-6 text-sm text-muted-foreground">
              I can help you understand your analytics data, find insights, and answer questions.
            </p>

            {/* Suggestions */}
            <div className="w-full space-y-2">
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInput(suggestion)}
                  className="glass-panel-sm w-full p-3 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {conversation.messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex gap-3',
                  message.role === 'user' ? 'flex-row-reverse' : ''
                )}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    message.role === 'user' ? 'bg-primary/10' : 'bg-muted'
                  )}
                >
                  {message.role === 'user' ? (
                    <User className="h-4 w-4 text-primary" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div
                  className={cn(
                    'glass-panel-sm max-w-[80%] rounded-2xl px-4 py-2.5',
                    message.role === 'user' ? 'bg-primary/10' : ''
                  )}
                >
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              </div>
            ))}

            {/* Streaming indicator */}
            {isStreaming && (
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="glass-panel-sm flex items-center gap-2 rounded-2xl px-4 py-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">
                    {streamingContent || 'Thinking...'}
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-border p-4">
        <div className="glass-panel-sm flex items-end gap-2 p-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your analytics..."
            rows={1}
            className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            style={{ maxHeight: '120px' }}
          />
          <button
            type="submit"
            disabled={!input.trim() || isStreaming}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
              input.trim() && !isStreaming
                ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                : 'bg-muted text-muted-foreground'
            )}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          ChartZen AI can make mistakes. Verify important data.
        </p>
      </form>
    </div>
  );
}
